// Fixed English structure the Ask model writes (section headings + action chips).
// The server keeps the model writing these verbatim in every language (completion
// detection, WhatsApp summary, listing PDF and the ask.vue chip matcher parse them);
// the UI swaps them for translations at render time only.

export const ASK_HEADINGS = {
  insidersVerdict: ['Insiders Verdict', "Insider's Verdict"],
  listingSummary: ['Listing Summary'],
  dldMarketBenchmark: ['DLD Market Benchmark'],
  marketValuationStatus: ['Market Valuation Status'],
  dldBenchmarkStatus: ['DLD Benchmark Status'],
  dldBenchmarkConfidence: ['DLD Benchmark Confidence'],
  whatThisMeans: ['What this means for the client'],
  dataConfidence: ['Data Confidence'],
  directAnswer: ['Direct Answer'],
  yieldPerformance: ['Yield & Performance'],
  topRecommendations: ['Top Recommendations'],
  recommendedMatches: ['Recommended Matches'],
  myTopPicks: ['My top 3 picks'],
  beforeYouPitch: ['Before you pitch, ask your client'],
  whatNext: ['What would you like to do next?'],
  missingRequiredFields: ['Missing required fields'],
  pricePosition: ['Price Position'],
  rentPosition: ['Rent Position'],
  buildingProjectContext: ['Building / Project Context'],
  rentalYield: ['Rental Yield'],
} as const satisfies Record<string, readonly string[]>;

export const ASK_CHIPS = {
  createWhatsappPitch: 'Create WhatsApp pitch for your client',
  whatsappRecommendation: 'Create a WhatsApp message for this recommendation',
  whatsappListing: 'Create a WhatsApp message about this listing for a client',
  whatsappValuation: 'Create a WhatsApp message about this valuation for a client',
  whatsappAreaBenchmark: 'Create a WhatsApp message about this area benchmark for a client',
  whatsappLaunches: 'Create a WhatsApp client summary for these recent launches',
  compareCheaper: 'Compare with cheaper options',
  saveSearch: 'Save this search',
  saveSearchToClient: 'Save this search to client',
  saveLaunch: 'Save this launch to a client profile',
  searchAlternativeAreas: 'Search alternative areas if budget is tight',
  requestAvailability: 'Request latest availability',
  submitClientAsDeal: 'Submit client as deal',
} as const satisfies Record<string, string>;

export type AskHeadingId = keyof typeof ASK_HEADINGS;
export type AskChipId = keyof typeof ASK_CHIPS;

// Leading emoji / markdown / arrow / bullet decorations, or a "1." / "2)" list number.
const PREFIX_RE = /^(?:[\p{Extended_Pictographic}\uFE0F\u200D#>*→•·\-–—]+\s*|\d{1,2}[.)]\s+)*/u;
const TRAILING_COLON_RE = /\s*:\s*$/;

const normalize = (value: string): string => value.replace(/[‘’]/g, "'").replace(/\s+/g, ' ').trim().toLowerCase();

const HEADING_LOOKUP = new Map<string, AskHeadingId>(
  (Object.entries(ASK_HEADINGS) as [AskHeadingId, readonly string[]][]).flatMap(([id, variants]) =>
    variants.map((variant) => [normalize(variant), id] as const),
  ),
);
const CHIP_LOOKUP = new Map<string, AskChipId>(
  (Object.entries(ASK_CHIPS) as [AskChipId, string][]).map(([id, text]) => [normalize(text), id]),
);

type LabelMatch<Id> = { id: Id; prefix: string; suffix: string };

const splitDecorations = (text: string, allowColon: boolean) => {
  const trimmed = text.trim();
  const prefix = trimmed.match(PREFIX_RE)?.[0] ?? '';
  const rest = trimmed.slice(prefix.length);
  const suffix = allowColon ? (rest.match(TRAILING_COLON_RE)?.[0] ?? '') : '';
  return { prefix, core: rest.slice(0, rest.length - suffix.length), suffix };
};

/** Exact (case-insensitive) match of a whole heading, allowing emoji/number prefixes and a trailing colon. */
export const matchAskHeading = (text: string): LabelMatch<AskHeadingId> | null => {
  const { prefix, core, suffix } = splitDecorations(text, true);
  const id = HEADING_LOOKUP.get(normalize(core));
  return id ? { id, prefix, suffix } : null;
};

/** Exact (case-insensitive) match of a whole chip line, allowing emoji/arrow prefixes. */
export const matchAskChip = (text: string): LabelMatch<AskChipId> | null => {
  const { prefix, core } = splitDecorations(text, false);
  const id = CHIP_LOOKUP.get(normalize(core));
  return id ? { id, prefix, suffix: '' } : null;
};

/**
 * Display text for a known heading/chip in the UI language, or null when the text
 * is not one of them. Decorations (emoji, numbering, trailing colon) are preserved.
 */
export const localizeAskLabel = (
  text: string,
  kind: 'heading' | 'chip',
  translate: (key: string) => string,
): string | null => {
  const match = kind === 'heading' ? matchAskHeading(text) : matchAskChip(text);
  if (!match) return null;
  const namespace = kind === 'heading' ? 'ask.headings' : 'ask.chips';
  return `${match.prefix}${translate(`${namespace}.${match.id}`)}${match.suffix}`;
};
